import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    CheckboxField,
    DatePickerField,
    Form as FormikForm,
    InputField,
    SelectField,
    TextareaField,
} from "components/common_components/Formik";
import Grid from "@material-ui/core/Grid";
import * as Yup from "yup";
import statuses from "utils/static/statuses";
import Status from "components/common_components/Status";
import { MenuItem } from "@material-ui/core";
import useFilesUploader, { FileTypes } from "components/common_components/hooks/useFilesUploader/useFilesUploader";
import { hasElements } from "utils/arrays";
import FormFieldsMapping from "./FormFieldsMapping";
import { Field } from "formik";
import { useDispatch } from "react-redux";
import AppContext from "AppContext";
import { getDownloadURL } from "firebase/storage";
import { notifyError } from "redux/actions/Notifications";
import Categories from "./Categories";
import Issuers from "./Issuers";

function AddForm({ data, resources, mode, onSubmit, ...rest }) {
    const dispatch = useDispatch();
    const [pdfUrl, setPdfUrl] = useState(null);

    const {
        api: {
            General: { Storage },
        },
    } = useContext(AppContext);

    const [filesToUpload, , , , uploader] = useFilesUploader({
        requestOrigin: false,
    });

    useEffect(() => {
        if (mode === "add") {
            if (hasElements(filesToUpload)) {
                const pdfUrl = URL.createObjectURL(filesToUpload[0]);
                setPdfUrl(pdfUrl);
            } else setPdfUrl(null);
        }
    }, [mode, filesToUpload]);

    useEffect(() => {
        if (data) {
            if (mode === "update") {
                const dirName = `settings/forms/pdf-files/${data.issuer.id}/${data.category.id}`;
                const fileName = `${data.code}_${data.version}.pdf`;
                getFileUrlAsync(dirName, fileName);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, data]);

    async function getFileUrlAsync(dirName, fileName) {
        try {
            const fUrl = await Storage.getDownloadURLAsync({
                dirName,
                fileName,
            });
            setPdfUrl(fUrl);
        } catch (message) {}
    }

    const validationSchema = Yup.lazy((values) =>
        Yup.object({
            issuer: Yup.object().required("Required field").nullable(),
            category: Yup.object().required("Required field").nullable(),
            code: Yup.string().required("Required field").max(50, "Max length is 50 characters"),
            version: Yup.string().required("Required field").max(50, "Max length is 50 characters"),
            expires: Yup.date().required("Required field").nullable(),
            extraInfo: Yup.string().notRequired().max(1000, "Max length is 1000 characters"),
            officialUrl: Yup.string().notRequired().max(1000, "Max length is 1000 characters"),
            isCurrent: Yup.boolean().notRequired().nullable(),
            status: Yup.string().required("Required field").max(50, "Max length is 50 characters"),
            fileUrl: Yup.string().notRequired(),
            formFieldMappings: Yup.array().min(1, "A PDF form file is required.").required("A PDF form file is required."),
        })
    );

    const initialValues = {
        id: data.id,
        issuer: data.issuer,
        category: data.category,
        code: data.code,
        version: data.version,
        expires: data.expires,
        extraInfo: data.extraInfo,
        officialUrl: data.officialUrl,
        isCurrent: data.isCurrent,
        status: data.status,
        formFieldMappings: data.formFieldMappings,
    };

    const submit = async (values, form) => {
        try {
            let newFileUrl = pdfUrl;
            if (mode === "add") {
                const snap = await Storage.uploadAsync({
                    path: `settings/forms/pdf-files/${values.issuer.id}/${values.category.id}/${values.code}_${values.version}.pdf`,
                    file: filesToUpload[0],
                });
                newFileUrl = await getDownloadURL(snap.ref);
            }
            const payload = {
                ...values,
                fileUrl: newFileUrl,
            };
            onSubmit(payload, form);
        } catch (error) {
            dispatch(notifyError(error.response?.data?.message || error.message || "Unexpected error."));
        }
    };

    return (
        <FormikForm initialValues={initialValues} validationSchema={validationSchema} onSubmit={submit} {...rest}>
            {(form) => (
                <Grid container spacing={3} direction="column">
                    <Grid item xs={12}>
                        <Grid container spacing={3} alignItems="center" className="identity">
                            <Grid item md={12} lg={6}>
                                <Field name="issuer">
                                    {() => (
                                        <Issuers
                                            issuers={resources.issuers}
                                            visible
                                            selected={form.values.issuer}
                                            onChange={(issuer) => form.setFieldValue("issuer", issuer)}
                                            onBackClick={() => form.setFieldValue("issuer", null)}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item md={12} lg={8}>
                                <Field name="category">
                                    {() => (
                                        <Categories
                                            categories={resources.categories}
                                            visible={Boolean(form.values.issuer)}
                                            selected={form.values.category}
                                            onChange={(category) => form.setFieldValue("category", category)}
                                            onBackClick={() => form.setFieldValue("category", null)}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            {form.values.issuer && form.values.category && (
                                <>
                                    <Grid item sm={12} md={6}>
                                        <InputField fieldName="code" label="Code *" maxLength={50} />
                                    </Grid>
                                    <Grid item sm={12} md={6}>
                                        <InputField fieldName="version" label="Version *" maxLength={50} />
                                    </Grid>
                                    <Grid item sm={12} md={6}>
                                        <DatePickerField fieldName="expires" label="Expires *" />
                                    </Grid>
                                    <Grid item sm={12} md={6}>
                                        <InputField fieldName="officialUrl" label="Official URL" maxLength={1000} />
                                    </Grid>
                                    <Grid item sm={12} md={6}>
                                        <SelectField
                                            disabled={mode === "update"}
                                            fieldName="status"
                                            label="Status *"
                                            options={[
                                                { id: "active", ...statuses.active },
                                                { id: "inactive", ...statuses.inactive },
                                            ]}
                                            labelKey="label"
                                            valueKey="id"
                                            renderOption={(option, index) => (
                                                <MenuItem value={option.id} key={index}>
                                                    <Status id={option.id} withIcon />
                                                </MenuItem>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item sm={12} md={6}>
                                        <CheckboxField fieldName="isCurrent" label="Is current" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextareaField fieldName="extraInfo" label="Extra info" maxLength={1000} rows={4} />
                                    </Grid>
                                    {mode === "add" && (
                                        <Grid item xs={12}>
                                            {uploader({
                                                acceptedFiles: [FileTypes.PDF],
                                                filesLimit: 1,
                                            })}
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Field name="formFieldMappings">
                                            {({ field, form: { errors, setFieldValue, setFieldError } }) => (
                                                <FormFieldsMapping
                                                    mode={mode}
                                                    pdfUrl={pdfUrl}
                                                    value={field.value}
                                                    onChange={(val) => {
                                                        setFieldValue(field.name, val);
                                                    }}
                                                    onError={(error) => {
                                                        setFieldError(field.name, error);
                                                    }}
                                                    error={errors.formFieldMappings}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </FormikForm>
    );
}

AddForm.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.number,
        issuer: PropTypes.shape({
            id: PropTypes.string,
            issuer: PropTypes.string,
            description: PropTypes.string,
        }),
        category: PropTypes.shape({
            id: PropTypes.string,
            category: PropTypes.string,
            description: PropTypes.string,
            parentCategory: PropTypes.shape({
                id: PropTypes.string,
                category: PropTypes.string,
            }),
        }),
        code: PropTypes.string,
        version: PropTypes.string,
        expires: PropTypes.instanceOf(Date),
        extraInfo: PropTypes.string,
        officialUrl: PropTypes.string,
        isCurrent: PropTypes.bool,
        status: PropTypes.string,
        fileUrl: PropTypes.string,
        formFieldMappings: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                formId: PropTypes.number,
                pdfField: PropTypes.string,
                htmlField: PropTypes.string,
                type: PropTypes.string,
                clientField: PropTypes.string,
            })
        ),
    }),
    resources: PropTypes.shape({
        issuers: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                issuer: PropTypes.string,
                description: PropTypes.string,
            })
        ),
        categories: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                category: PropTypes.string,
                description: PropTypes.string,
                parentCategory: PropTypes.shape({
                    id: PropTypes.string,
                    category: PropTypes.string,
                }),
            })
        ),
    }),
    mode: PropTypes.oneOf(["add", "update"]).isRequired,
};

AddForm.defaultProps = {
    data: {
        id: null,
        issuer: null,
        category: null,
        code: "",
        version: "",
        expires: null,
        extraInfo: "",
        officialUrl: "",
        isCurrent: false,
        status: "active",
        fileUrl: null,
        formFieldMappings: [],
    },
    resources: {
        issuers: [],
        categories: [],
    },
};

export default AddForm;
